import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  GlobeAltIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';
import api, { mlszAPI, profileMlszAPI, MlszStepResult, MlszPlayerData, MlszImportResult, MlszClub } from '../services/api';
import FilterAccordion from '../components/ui/FilterAccordion';
import LazyLoadTrigger from '../components/ui/LazyLoadTrigger';
import { useBatchedList } from '../hooks/useBatchedList';
import { useClubSettings } from '../contexts/ClubSettingsContext';
import { useToast } from '../contexts/ToastContext';
import { getErrorMessage } from '../utils/errorMessage';
import { matchesSearch } from '../utils/search';
import { useEscapeToClose } from '../hooks/useEscapeToClose';

const ALL_STEPS = [
  'Kapcsolódás az ifa.mlsz.hu-hoz...',
  'Azure bejelentkezési oldal feldolgozása...',
  'Email cím elküldése...',
  'Bejelentkezés...',
  'Játékosok oldal betöltése...',
  'Játékosadatok lekérdezése...',
];

const IFA: React.FC = () => {
  usePageTitle('IFA');
  const { settings: clubSettings, refreshSettings } = useClubSettings();
  const { showToast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [steps, setSteps] = useState<MlszStepResult[]>([]);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [players, setPlayers] = useState<MlszPlayerData[]>([]);
  // Egy MLSZ fiók több egyesülethez is tartozhat - ilyenkor választani kell
  const [clubs, setClubs] = useState<MlszClub[]>([]);
  const [selectedClubId, setSelectedClubId] = useState<string>('');
  const [clubSelectionRequired, setClubSelectionRequired] = useState(false);
  // A kötött egyesület nem tartozik a fiókhoz: csak kifejezett választással lehet átváltani
  const [clubMismatch, setClubMismatch] = useState(false);
  // Ha az egyesületválasztót nem sikerült azonosítani az MLSZ oldalán
  const [availableSelects, setAvailableSelects] = useState<string[]>([]);
  const [existingIfaCodes, setExistingIfaCodes] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [hasRun, setHasRun] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  useEscapeToClose(showConfirm, () => setShowConfirm(false));
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<MlszImportResult | null>(null);
  const [search, setSearch] = useState('');
  const [expandedPlayers, setExpandedPlayers] = useState<Set<string>>(new Set());

  // A Profil oldalon mentett IFA belépés. Ha van, nem kell itt újra beírni.
  const [savedEmail, setSavedEmail] = useState<string | null>(null);
  const [storageAvailable, setStorageAvailable] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [useOtherAccount, setUseOtherAccount] = useState(false);
  const [saveToProfile, setSaveToProfile] = useState(true);
  const usingSaved = savedEmail !== null && !useOtherAccount;

  useEffect(() => {
    api
      .get('/profile')
      .then(({ data }) => {
        setSavedEmail(data.hasMlszCredentials && data.mlszEmail ? data.mlszEmail : null);
        setStorageAvailable(data.mlszCredentialStorageAvailable === true);
      })
      .catch(() => {
        // Profil nélkül is működik az oldal - csak kézzel kell beírni a belépést
      })
      .finally(() => setProfileLoaded(true));
  }, []);

  /** Üres email és jelszó: a szerver a Profilban mentett belépést használja. */
  const credentials = (): [string, string] => (usingSaved ? ['', ''] : [email, password]);

  const toggleExpanded = (regKod: string) => {
    setExpandedPlayers((prev) => {
      const next = new Set(prev);
      if (next.has(regKod)) next.delete(regKod);
      else next.add(regKod);
      return next;
    });
  };

  const existingCodes = new Set(existingIfaCodes);
  const filteredPlayers = players.filter(p => matchesSearch(search, p.nev, p.regKod));
  // A "mind kijelölése" a látható (szűrt) listára hat - a szűrésen kívüli játékost nem jelöl ki észrevétlenül
  const importablePlayers = filteredPlayers.filter(p => !existingCodes.has(p.regKod));
  const allImportableSelected = importablePlayers.length > 0 && importablePlayers.every(p => selectedIds.has(p.regKod));
  const selectedClub = clubs.find((c) => c.id === selectedClubId) ?? null;

  // Mobil kártyalista lapozása (5-ösével)
  const {
    visible: visibleMobilePlayers,
    hasMore,
    loadMore,
  } = useBatchedList(filteredPlayers, 5);

  // Asztali táblázat: 800 soros IFA keretnél is adagokban
  const {
    visible: visibleDesktopPlayers,
    hasMore: hasMoreDesktop,
    loadMore: loadMoreDesktop,
  } = useBatchedList(filteredPlayers, 60);

  /**
   * Lekérdezés az MLSZ-től. Egyesületválasztás után ugyanez fut le újra a
   * választott egyesülettel - az MLSZ munkamenet nem él túl két hívást,
   * ezért minden lekérdezés újra bejelentkezik.
   */
  const runLogin = async (clubId?: string) => {
    setLoading(true);
    setSteps([]);
    setPlayers([]);
    setExistingIfaCodes([]);
    setSelectedIds(new Set());
    setError(null);
    setImportResult(null);
    setClubSelectionRequired(false);
    setClubMismatch(false);
    setAvailableSelects([]);
    setHasRun(true);
    setCurrentStep(1);

    const stepInterval = setInterval(() => {
      setCurrentStep((prev) => {
        if (prev < ALL_STEPS.length) return prev + 1;
        return prev;
      });
    }, 2000);

    try {
      const response = await mlszAPI.login(...credentials(), clubId);

      // Kézzel beírt, működő belépés: ha kérte, elmentjük a Profilba, hogy legközelebb ne kelljen beírni
      if (!usingSaved && saveToProfile && storageAvailable && (response.success || response.clubSelectionRequired)) {
        try {
          await profileMlszAPI.save(email.trim(), password);
          setSavedEmail(email.trim());
          setUseOtherAccount(false);
          setPassword('');
          showToast('Az IFA belépést elmentettük a Profilodba.', 'success');
        } catch {
          showToast('Az IFA belépést nem sikerült elmenteni - legközelebb újra be kell írni.', 'error');
        }
      }

      clearInterval(stepInterval);
      setSteps(response.steps);
      setCurrentStep(response.steps.length);
      setClubs(response.clubs || []);
      setAvailableSelects(response.availableSelects || []);

      if (response.selectedClubId) {
        setSelectedClubId(response.selectedClubId);
      }

      // Több egyesület: a felhasználó választ, utána újra lefut a lekérdezés
      if (response.clubSelectionRequired) {
        setClubSelectionRequired(true);
        if (!clubId && response.clubs?.length) {
          setSelectedClubId(response.clubs[0].id);
        }
        return;
      }

      if (response.clubMismatch) {
        setClubMismatch(true);
        if (response.clubs?.length) {
          setSelectedClubId(response.clubs[0].id);
        }
      }

      if (!response.success) {
        setError(response.error || 'Ismeretlen hiba történt');
      }

      setPlayers(response.players || []);
      setExistingIfaCodes(response.existingIfaCodes || []);

      // A szerver megjegyezte a használt egyesületet - a beállítások frissítése
      if (response.success && response.selectedClubId !== clubSettings?.mlszClubId) {
        refreshSettings();
      }
    } catch (err: any) {
      clearInterval(stepInterval);
      setError(
        err.response?.data?.error ||
          err.message ||
          'Hálózati hiba - nem sikerült elérni a szervert'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!usingSaved && (!email || !password)) return;
    await runLogin();
  };

  const togglePlayer = (regKod: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(regKod)) next.delete(regKod);
      else next.add(regKod);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      importablePlayers.forEach((p) => (allImportableSelected ? next.delete(p.regKod) : next.add(p.regKod)));
      return next;
    });
  };

  const handleImport = async () => {
    setShowConfirm(false);
    setImporting(true);
    setError(null);

    const playersToImport = players.filter(p => selectedIds.has(p.regKod));

    try {
      const result = await mlszAPI.importPlayers(...credentials(), playersToImport, selectedClubId);
      setImportResult(result);

      if (result.importedCount > 0) {
        const skipped = result.skippedCount > 0 ? `, ${result.skippedCount} kihagyva` : '';
        showToast(
          `${result.importedCount} játékos importálva${skipped}.`,
          'success'
        );
      } else if (result.skippedCount > 0) {
        showToast(
          `Nem történt import: mind a ${result.skippedCount} játékos már létezik.`,
          'info'
        );
      } else {
        showToast('Egy játékos sem került importálásra.', 'info');
      }

      // Mark imported players as existing
      setExistingIfaCodes(prev => [...prev, ...playersToImport.map(p => p.regKod)]);
      setSelectedIds(new Set());
    } catch (err: any) {
      const message = getErrorMessage(err, 'Hiba történt az importálás során.');
      setError(message);
      showToast(message, 'error');
    } finally {
      setImporting(false);
    }
  };

  const getStepIcon = (stepIndex: number) => {
    const stepNum = stepIndex + 1;
    const stepResult = steps.find((s) => s.step === stepNum);

    if (stepResult) {
      if (stepResult.success) {
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      } else if (stepResult.error) {
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      }
    }

    if (loading && stepNum === currentStep) {
      return <ArrowPathIcon className="h-5 w-5 text-primary-500 animate-spin" />;
    }

    if (loading && stepNum < currentStep) {
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    }

    return (
      <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
    );
  };

  const getStepTextClass = (stepIndex: number) => {
    const stepNum = stepIndex + 1;
    const stepResult = steps.find((s) => s.step === stepNum);

    if (stepResult?.error) return 'text-red-600';
    if (stepResult?.success) return 'text-green-700';
    if (loading && stepNum === currentStep) return 'text-primary-700 font-medium';
    if (loading && stepNum < currentStep) return 'text-green-700';
    return 'text-gray-400';
  };

  return (
    <div>
      <div className="flex items-center mb-6">
        <GlobeAltIcon className="h-7 w-7 text-primary-600 mr-3" />
        <h1 className="text-2xl font-bold text-gray-900">IFA - MLSZ Integráció</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Login Form */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Bejelentkezés</h2>

          {!profileLoaded ? (
            <p className="text-sm text-gray-400">Betöltés...</p>
          ) : usingSaved ? (
            /* Mentett belépés: egy gombnyomás */
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-gray-600">
                A Profilodban mentett IFA fiókkal lépünk be:{' '}
                <span className="font-medium text-gray-900">{savedEmail}</span>
              </p>
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-md bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Bejelentkezés folyamatban...' : 'Bejelentkezés és lekérdezés'}
              </button>
              <div className="flex flex-wrap justify-between gap-2 text-sm">
                <button
                  type="button"
                  onClick={() => {
                    setUseOtherAccount(true);
                    // Másik fiók: alapból ne írja felül a mentett belépést
                    setSaveToProfile(false);
                  }}
                  disabled={loading}
                  className="text-gray-500 hover:text-gray-700"
                >
                  Másik fiókkal lépek be
                </button>
                <Link to="/profile" className="text-primary-700 hover:text-primary-800">
                  Módosítás a Profilban
                </Link>
              </div>
            </form>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-6">
                Add meg az MLSZ IFA fiókod email címét és jelszavát a bejelentkezéshez.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="mlsz-email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email cím
                  </label>
                  <input
                    id="mlsz-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="pelda@email.hu"
                    autoComplete="username"
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
                    disabled={loading}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="mlsz-password" className="block text-sm font-medium text-gray-700 mb-1">
                    Jelszó
                  </label>
                  <input
                    id="mlsz-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
                    disabled={loading}
                    required
                  />
                </div>

                {storageAvailable && (
                  <label className="flex items-start gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={saveToProfile}
                      onChange={(e) => setSaveToProfile(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600"
                      disabled={loading}
                    />
                    <span>
                      {savedEmail !== null
                        ? 'Ez legyen a mentett belépésem a Profilban'
                        : 'Mentés a Profilomba, hogy legközelebb ne kelljen beírni'}
                      <span className="block text-xs text-gray-500">
                        A jelszót titkosítva tároljuk, és csak az IFA felé használjuk.
                      </span>
                    </span>
                  </label>
                )}

                <button
                  type="submit"
                  disabled={loading || !email || !password}
                  className="w-full rounded-md bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Bejelentkezés folyamatban...' : 'Bejelentkezés és lekérdezés'}
                </button>

                {savedEmail !== null && (
                  <button
                    type="button"
                    onClick={() => setUseOtherAccount(false)}
                    disabled={loading}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Vissza a mentett fiókhoz ({savedEmail})
                  </button>
                )}
              </form>
            </>
          )}
        </div>

        {/* Steps Panel */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Folyamat</h2>

          {!hasRun ? (
            <p className="text-sm text-gray-400">
              A bejelentkezés indítása után itt követheted a folyamat lépéseit.
            </p>
          ) : (
            <div className="space-y-3">
              {ALL_STEPS.map((stepDesc, index) => {
                const stepResult = steps.find((s) => s.step === index + 1);
                return (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getStepIcon(index)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${getStepTextClass(index)}`}>
                        {index + 1}. {stepDesc}
                      </p>
                      {stepResult?.error && (
                        <p className="text-xs text-red-500 mt-1">{stepResult.error}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {error && !loading && (
            <div className="mt-4 p-3 bg-red-50 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {players.length > 0 && !loading && (
            <div className="mt-4 p-3 bg-green-50 rounded-lg">
              <p className="text-sm text-green-700 font-medium">
                {players.length} játékos betöltve
                {existingIfaCodes.length > 0 && ` (${existingIfaCodes.length} már létezik)`}
              </p>
            </div>
          )}

          {importResult && (
            <div className="mt-4 p-3 bg-green-50 rounded-lg">
              <p className="text-sm text-green-700 font-medium">
                {importResult.importedCount} játékos sikeresen importálva!
              </p>
              {importResult.skippedCount > 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  {importResult.skippedCount} kihagyva (már létezik)
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Ha az MLSZ átalakította az oldalt, itt látszik, mit talált a rendszer */}
      {availableSelects.length > 0 && !loading && (
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm font-medium text-amber-900">
            Az egyesületválasztót nem sikerült azonosítani az MLSZ oldalán
          </p>
          <p className="text-sm text-amber-800 mt-1">
            A lekérdezés az IFA-ban alapértelmezett egyesülettel futott. Az oldalon talált
            legördülők:
          </p>
          <ul className="mt-2 text-xs font-mono text-amber-900 space-y-0.5">
            {availableSelects.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Egyesületválasztó - egy MLSZ fiók több egyesülethez is tartozhat */}
      {(clubs.length > 1 || (clubMismatch && clubs.length > 0)) && !loading && (
        <div className="mt-6 bg-white rounded-lg shadow p-4 md:p-6">
          <div className="flex items-center mb-3">
            <BuildingOffice2Icon className="h-6 w-6 text-primary-600 mr-2 flex-shrink-0" />
            <h2 className="text-lg font-semibold text-gray-900">Egyesület</h2>
          </div>

          <p className="text-sm text-gray-500 mb-4">
            {clubMismatch
              ? `A rendszer a(z) ${clubSettings?.mlszClubName || 'korábban kiválasztott'} egyesülethez van kötve, de ez az IFA fiók nem tartozik hozzá. Ha ezentúl ennek a fióknak az egyesületével dolgoznál, válaszd ki - a kötés felülíródik.`
              : clubSelectionRequired
                ? `Ehhez a fiókhoz ${clubs.length} egyesület tartozik. Válaszd ki, melyik játékosait szeretnéd lekérdezni - a választás megjegyzésre kerül.`
                : 'A rendszer ehhez az egyesülethez van kötve. Másik egyesület választásával a beállítás felülíródik.'}
          </p>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label htmlFor="mlsz-club" className="block text-sm font-medium text-gray-700 mb-1">
                Egyesület
              </label>
              <select
                id="mlsz-club"
                value={selectedClubId}
                onChange={(e) => setSelectedClubId(e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
              >
                {clubs.map((club) => (
                  <option key={club.id} value={club.id}>
                    {club.name}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={() => runLogin(selectedClubId)}
              disabled={!selectedClubId}
              className="w-full sm:w-auto rounded-md bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Játékosok lekérdezése
            </button>
          </div>
        </div>
      )}

      {/* Import Selection Table */}
      {players.length > 0 && !loading && (
        <div className="mt-6 bg-white rounded-lg shadow p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Játékosok importálása ({players.length} fő)
              </h2>
              {selectedClub && (
                <p className="text-sm text-gray-500 mt-0.5">{selectedClub.name}</p>
              )}
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Keresés név alapján..."
              className="hidden md:block rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500 w-full sm:w-64"
            />
            {selectedIds.size > 0 && (
              <button
                onClick={() => setShowConfirm(true)}
                disabled={importing}
                className="w-full sm:w-auto rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importing ? 'Importálás...' : `${selectedIds.size} játékos importálása`}
              </button>
            )}
          </div>

          {/* Mobil szűrő (md alatt) */}
          <div className="md:hidden mb-4">
            <FilterAccordion activeCount={search ? 1 : 0}>
              <div className="pt-2 pb-1">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Keresés név alapján..."
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500"
                />
              </div>
            </FilterAccordion>
          </div>

          {/* Mobil kártyalista (md alatt) */}
          <div className="md:hidden">
            {importablePlayers.length > 0 && (
              <label className="flex items-center gap-2 mb-3 px-1 py-1 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={allImportableSelected}
                  onChange={toggleAll}
                  className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                {search ? `A ${importablePlayers.length} találat kijelölése` : 'Összes importálható kijelölése'}
              </label>
            )}
            {filteredPlayers.length === 0 ? (
              <div className="text-center py-6 text-sm text-gray-500">Nincs találat.</div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  {visibleMobilePlayers.map((player) => {
                    const isExisting = existingCodes.has(player.regKod);
                    return (
                      <div
                        key={player.id}
                        className={`bg-white rounded-lg shadow border p-3 flex flex-col ${
                          isExisting
                            ? 'opacity-60'
                            : selectedIds.has(player.regKod)
                            ? 'border-primary-300 bg-primary-50'
                            : ''
                        }`}
                      >
                        <div className="flex items-start justify-between gap-1 mb-1">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(player.regKod)}
                            onChange={() => togglePlayer(player.regKod)}
                            disabled={isExisting}
                            className="mt-0.5 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-30"
                          />
                          {isExisting ? (
                            <span className="inline-flex flex-shrink-0 items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                              Már létezik
                            </span>
                          ) : (
                            <span className="inline-flex flex-shrink-0 items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                              Importálható
                            </span>
                          )}
                        </div>
                        <div className="min-w-0 mb-1.5">
                          <div className="font-medium text-sm text-gray-900 break-words hyphens-auto leading-snug">{player.nev}</div>
                          <div className="text-xs text-gray-500 truncate">{player.regKod}</div>
                        </div>
                        <div className="space-y-0.5 text-xs text-gray-600">
                          <div className="truncate">
                            <span className="text-gray-400">Szül.:</span> {player.szulDatum}
                          </div>
                          <div className="truncate">
                            <span className="text-gray-400">Anyja neve:</span> {player.anyjaNeve}
                          </div>
                          <div className="truncate">
                            <span className="text-gray-400">Sportorvosi érv.:</span>{' '}
                            {player.sportorvosiErvenyesseg || '-'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {hasMore && <LazyLoadTrigger key={visibleMobilePlayers.length} onLoadMore={loadMore} />}
              </>
            )}
          </div>

          {/* Desktop táblázat (md alatt rejtve) */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">
                    <input
                      type="checkbox"
                      checked={allImportableSelected}
                      title={search ? `A ${importablePlayers.length} találat kijelölése` : 'Összes importálható kijelölése'}
                      onChange={toggleAll}
                      disabled={importablePlayers.length === 0}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Név</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">IFA kód</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Születési dátum</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Anyja neve</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sportorvosi érv.</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Státusz</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {visibleDesktopPlayers.map((player) => {
                  const isExisting = existingCodes.has(player.regKod);
                  return (
                    <tr
                      key={player.id}
                      className={isExisting ? 'bg-gray-50 opacity-60' : selectedIds.has(player.regKod) ? 'bg-primary-50' : 'hover:bg-gray-50'}
                    >
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(player.regKod)}
                          onChange={() => togglePlayer(player.regKod)}
                          disabled={isExisting}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-30"
                        />
                      </td>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900">{player.nev}</td>
                      <td className="px-4 py-2 text-sm text-gray-500">{player.regKod}</td>
                      <td className="px-4 py-2 text-sm text-gray-500">{player.szulDatum}</td>
                      <td className="px-4 py-2 text-sm text-gray-500">{player.anyjaNeve}</td>
                      <td className="px-4 py-2 text-sm text-gray-500">{player.sportorvosiErvenyesseg || '-'}</td>
                      <td className="px-4 py-2 text-sm">
                        {isExisting ? (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                            Már létezik
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                            Importálható
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {hasMoreDesktop && <LazyLoadTrigger key={visibleDesktopPlayers.length} onLoadMore={loadMoreDesktop} />}
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-label="Játékosok importálása">
          <div className="flex min-h-full items-stretch justify-center md:items-center md:p-4">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity duration-300"
              onClick={() => setShowConfirm(false)}
            />
            <div className="modal-panel-mobile relative flex w-full flex-col bg-white shadow-xl md:max-w-lg md:rounded-lg">
              <div className="flex shrink-0 items-center justify-between border-b border-gray-200 p-4 md:border-0 md:p-6 md:pb-2">
                <h3 className="text-lg font-semibold text-gray-900">
                  Játékosok importálása
                </h3>
                <button
                  type="button"
                  onClick={() => setShowConfirm(false)}
                  className="flex h-9 w-9 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 md:hidden"
                  aria-label="Bezárás"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 md:px-6 md:pt-0 md:pb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Biztosan importálod az alábbi {selectedIds.size} játékost?
                </p>
                <p className="text-xs text-gray-400 mb-4">
                  A nem, a születési hely és ország, valamint az állampolgárság az MLSZ
                  játékos-adatlapjairól töltődik be az importálás során - ezek az MLSZ
                  játékoslistájában nem szerepelnek.
                </p>
                <div className="max-h-96 overflow-y-auto bg-gray-50 rounded-lg p-3">
                  <ul className="space-y-1">
                    {players
                      .filter(p => selectedIds.has(p.regKod))
                      .map(p => (
                        <li key={p.regKod} className="border-b border-gray-200 last:border-b-0">
                          <button
                            type="button"
                            onClick={() => toggleExpanded(p.regKod)}
                            className="w-full flex items-center justify-between py-2 px-1 text-sm text-gray-700 hover:bg-gray-100 rounded"
                          >
                            <span>
                              <span className="font-medium">{p.nev}</span>{' '}
                              <span className="text-gray-400">({p.regKod})</span>
                            </span>
                            {expandedPlayers.has(p.regKod) ? (
                              <ChevronUpIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            ) : (
                              <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            )}
                          </button>
                          {expandedPlayers.has(p.regKod) && (
                            <div className="px-1 pb-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                              <div><span className="font-medium text-gray-500">IFA kód:</span> {p.regKod}</div>
                              <div><span className="font-medium text-gray-500">Születési dátum:</span> {p.szulDatum || '-'}</div>
                              <div><span className="font-medium text-gray-500">Anyja neve:</span> {p.anyjaNeve || '-'}</div>
                              <div><span className="font-medium text-gray-500">TAJ szám:</span> {p.tajSzam || '-'}</div>
                              <div className="col-span-2"><span className="font-medium text-gray-500">Sportorvosi érv.:</span> {p.sportorvosiErvenyesseg || '-'}</div>
                            </div>
                          )}
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
              <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-200 p-4 pb-safe-4 md:flex-row md:justify-end md:gap-3 md:border-0 md:p-6 md:pt-0">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="w-full md:w-auto rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Mégsem
                </button>
                <button
                  onClick={handleImport}
                  className="w-full md:w-auto rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
                >
                  Importálás
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IFA;
